import React, { useState } from "react";

import { faUndoAlt } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";

import { patchSaleLot } from "actions";

import { MultiButton } from "components/Button";
import Dialog from "components/Dialog";

import { PreventPropagation } from "components/Events";
import { MultiButtonOption } from "components/Button/MultiButton/types";
import { SecondaryMultiButton } from "components/Button/MultiButton";
import {
  LotButtonDefaultOrder,
  LotButtonOption,
  useGetLotButtonOptions,
} from "./buttonOptions";
import { useMapButtonOptionToMultiButtons } from "./multiButtons";

interface SaleLotActionOptionsProps {
  saleLotId: string;
  preferredActionOrder?: LotButtonOption[];
}

export const SaleLotActionOptions = ({
  saleLotId,
  preferredActionOrder,
}: SaleLotActionOptionsProps) => {
  const [isUnsellDialogVisible, setIsUnsellDialogVisible] = useState(false);
  const dispatch = useDispatch();

  // Retrieve the list of button options for the given sale lot
  // This hook encapsulates the logic to determine which buttons should be shown
  // based on permissions, sale lot status, sale type, etc.
  const buttonOptions: LotButtonOption[] = useGetLotButtonOptions(saleLotId);

  // Reconstruct the available button options in the preferred order
  const workingOrder = [];
  if (Array.isArray(preferredActionOrder) && preferredActionOrder.length > 0) {
    preferredActionOrder.forEach(option => workingOrder.push(option));
  }

  // Now add any options from the default order that aren't already included
  LotButtonDefaultOrder.forEach(option => {
    if (!workingOrder.includes(option)) {
      workingOrder.push(option);
    }
  });

  // Sort the button options based union of preferred and default order
  buttonOptions.sort(
    (a, b) => workingOrder.indexOf(a) - workingOrder.indexOf(b),
  );

  // Map the button options to actual MultiButtonOption configurations
  const buttons: MultiButtonOption[] = useMapButtonOptionToMultiButtons(
    saleLotId,
    setIsUnsellDialogVisible,
    buttonOptions,
  );

  const unsell = () => {
    const patch = {
      total_price_cents: 0,
      buyer_id: null,
      buyer_way: null,
      id: saleLotId,
    };
    dispatch(patchSaleLot(patch, { changeReason: "Bulk unsold" }));
  };

  return (
    <PreventPropagation>
      <div>
        <MultiButton buttons={buttons} ButtonComponent={SecondaryMultiButton} />
      </div>
      {isUnsellDialogVisible && (
        <Dialog
          closeSelf={() => setIsUnsellDialogVisible(false)}
          handleSubmit={unsell}
          title="Unsell lot?"
          subtitle="Are you sure you want to undo this sale?"
          submitButtonText="Unsell"
          rejectButtonText="Cancel"
          icon={faUndoAlt}
        />
      )}
    </PreventPropagation>
  );
};
